import {
    Avatar,
    Alert,
    Chip,
    Grid2,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText
} from '@mui/material';
import { useEffect, useState } from 'react';

import { green, red, amber, grey } from '@mui/material/colors';
import { OnlineStatusEnum } from '../../../_common/enum/onlineStatus.enum';
import { getUserPresence, presenceLabel, secondsSince, summarizeRegisteredUserPresence } from '../../../_common/auctionPresence.helper';
import { StandardPanel } from '../../../_components/panel/standard.panel';
import { handleErrorResponse } from '../../../_helpers/handleResponse.helper';
import { MessageNotification } from '../../../_components/box/message-notification';
import { catalogueUserService } from '../../../_services/catalogueUser.service';
import { catalogueService } from '../../../_services/catalogueService.service';
import type { HeartbeatsMap, TabVisibleMap } from '../../../_hubs/auctionUserAdmin.hub';

function LastSeenChip(params: { userId: string; lastSeenByUserId?: HeartbeatsMap; presence: OnlineStatusEnum }) {
    const utcMs = params.lastSeenByUserId?.[params.userId];
    const [secondsAgo, setSecondsAgo] = useState<number | null>(utcMs != null ? secondsSince(utcMs) : null);

    useEffect(() => {
        if (utcMs == null) {
            setSecondsAgo(null);
            return;
        }
        const update = () => setSecondsAgo(secondsSince(utcMs));
        update();
        const t = setInterval(update, 1000);
        return () => clearInterval(t);
    }, [utcMs]);

    const hasData = secondsAgo != null;
    const isDanger = params.presence === OnlineStatusEnum.Offline;
    const isWarning = params.presence === OnlineStatusEnum.Away;
    const isHealthy = params.presence === OnlineStatusEnum.Online;
    const label = hasData
        ? (secondsAgo < 60 ? `${secondsAgo}s ago` : `${Math.floor(secondsAgo / 60)}m ago`)
        : presenceLabel(params.presence);

    const chipColor = isHealthy
        ? { bg: green[100], fg: green[800], border: green[500] }
        : isWarning
            ? { bg: amber[100], fg: amber[800], border: amber[500] }
            : isDanger
                ? { bg: red[100], fg: red[800], border: red[500] }
                : { bg: grey[100], fg: grey[800], border: grey[400] };

    return (
        <Chip
            size="small"
            label={label}
            sx={{
                flexShrink: 0,
                bgcolor: chipColor.bg,
                color: chipColor.fg,
                borderColor: chipColor.border,
                border: '1px solid',
            }}
        />
    );
}

function avatarColor(presence: OnlineStatusEnum) {
    if (presence === OnlineStatusEnum.Online) return green[500];
    if (presence === OnlineStatusEnum.Away) return amber[700];
    return red[500];
}

function secondaryColor(presence: OnlineStatusEnum) {
    if (presence === OnlineStatusEnum.Online) return 'success.main';
    if (presence === OnlineStatusEnum.Away) return 'warning.main';
    return 'error.main';
}

export function AuctionUserList(params: {
    catalogueId?: number;
    auctionId?: number;
    onlineUsers?: string[];
    lastSeenByUserId?: HeartbeatsMap;
    tabVisibleByUserId?: TabVisibleMap;
}) {

    const [data, setData] = useState<any>();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, [params.catalogueId, params.auctionId]);

    useEffect(() => {
        if (!!data && !!data.length) {
            setOnlineStatus();
        }
    }, [params.onlineUsers, params.lastSeenByUserId, params.tabVisibleByUserId]);

    function resolvePresence(user: any): OnlineStatusEnum {
        return getUserPresence({
            userId: user.userId,
            onlineUsers: params.onlineUsers,
            lastSeenByUserId: params.lastSeenByUserId,
            tabVisibleByUserId: params.tabVisibleByUserId,
        });
    }

    function applyOnlineStatus(users: any[]) {
        return users.map((user: any) => ({
            ...user,
            onlineStatusId: params.onlineUsers?.some((u) => u == user.userId)
                ? OnlineStatusEnum.Online
                : OnlineStatusEnum.Offline,
        }));
    }

    function setOnlineStatus() {
        setData((_d: any) => {
            let __d = [..._d];
            __d.forEach((___d) => {
                ___d.onlineStatusId = resolvePresence(___d);
            })

            return __d;
        });
    }

    function applyUsers(users: any[]) {
        const withStatus = (Array.isArray(users) ? users : []).map((user: any) => ({
            ...user,
            onlineStatusId: resolvePresence(user),
        }));
        setData(withStatus);
    }

    function fetchData() {
        if (!!params.catalogueId) {
            setLoading(true);
            return catalogueUserService.getAll(params.catalogueId)
                .then(
                    (data: any) => {
                        applyUsers(data);
                    }, handleErrorResponse)
                .finally(() => {
                    setLoading(false);
                });
        }

        if (!!params.auctionId) {
            setLoading(true);
            return catalogueService.getAllAuction(params.auctionId)
                .then(async (catalogues: any) => {
                    const catalogueIds = (Array.isArray(catalogues) ? catalogues : [])
                        .map((c: any) => Number(c?.id))
                        .filter((id: number) => id > 0);

                    const usersPerCatalogue = await Promise.all(
                        catalogueIds.map((id) => catalogueUserService.getAll(id).catch(() => [])),
                    );

                    const byUserId = new Map<string, any>();
                    usersPerCatalogue.forEach((users: any) => {
                        if (!Array.isArray(users)) return;
                        users.forEach((user: any) => {
                            const userId = String(user?.userId ?? '').trim();
                            if (!!userId && !byUserId.has(userId)) {
                                byUserId.set(userId, user);
                            }
                        });
                    });

                    applyUsers(Array.from(byUserId.values()));
                }, handleErrorResponse)
                .finally(() => {
                    setLoading(false);
                });
        }

        setLoading(false);
        return;
    }

    const presenceSummary = (!loading && !!data && !!data.length)
        ? summarizeRegisteredUserPresence(data, {
            onlineUsers: params.onlineUsers,
            lastSeenByUserId: params.lastSeenByUserId,
            tabVisibleByUserId: params.tabVisibleByUserId,
        })
        : null;

    return (
        <Grid2 container spacing={1}>
            <Grid2 size={{ xs: 12 }}>

                <StandardPanel title={!!data ? `Catalogue Users` : !!loading ? "Loading.." : undefined} loading={loading}>

                    {!!presenceSummary &&
                        <Alert severity={presenceSummary.severity} sx={{ mb: 1 }}>
                            {presenceSummary.message}
                        </Alert>
                    }

                    {(!loading && !!data && !!data.length) &&
                        <List disablePadding>
                            {data.map((user: any) => {
                                const presence = resolvePresence(user);
                                return (
                                    <ListItem key={user.id}
                                        disablePadding>

                                        <ListItemButton sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: avatarColor(presence) }} src={user.userName} alt={user.userName} />
                                            </ListItemAvatar>

                                            <ListItemText
                                                primary={user.userName}
                                                sx={{ flex: 1, "& .MuiListItemText-secondary": { color: secondaryColor(presence) } }}
                                                secondary={presenceLabel(presence)}
                                            />
                                            <LastSeenChip userId={user.userId} lastSeenByUserId={params.lastSeenByUserId} presence={presence} />
                                        </ListItemButton>


                                    </ListItem>
                                );
                            })}
                        </List>
                    }

                    {(!loading && (!data || !data.length)) &&
                        <MessageNotification message='No users associated with the auction' />
                    }

                </StandardPanel>
            </Grid2>

        </Grid2 >
    )
}
